import { Fragment } from 'react';
import { motion, type Variants } from 'framer-motion';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'next-i18next';
import { AiGenerateDockPendingCards } from '../features/ai-builder-chat/components/AiGenerateDockPendingCards';
import { aiGenerateDockSlotAtom } from '../features/ai-builder-chat/stores/aiGenerateDockRun.atoms';
import { SimilarQuestionPendingCard } from '../features/question-card-ai/components/SimilarQuestionPendingCard';
import { useResetQuestionCardAiSlots } from '../features/question-card-ai/hooks/useResetQuestionCardAiSlots';
import { useBuildFormContext } from '../form/schema';
import { EditableQuizCardWithProvider } from './EditableQuizCard/EditableQuizCardWithProvider';
import { QuizListSortableContainer } from './QuizListSortableContainer';
import { useQuizList } from './useQuizList';
type QuizListProps = {
  emptyDescriptionTransitionState?: 'visible' | 'hidden';
  showEmptyDescription?: boolean;
};
export const QuizList = ({
  emptyDescriptionTransitionState = 'visible',
  showEmptyDescription = true
}: QuizListProps) => {
  const {
    control
  } = useBuildFormContext();
  const {
    quizList,
    handleDragEnd
  } = useQuizList();
  const {
    t
  } = useTranslation(['build']);
  const generatorSlot = useAtomValue(aiGenerateDockSlotAtom);
  useResetQuestionCardAiSlots();

  // 빈 퀴즈에서 생성기 자리가 있으면 안내 줄 대신 대기·실패 카드를 보인다 — 첫 문제들이 들어올 자리다.
  if (!quizList.length && generatorSlot) {
    return <div className="mb-lg flex w-full flex-col">
        <AiGenerateDockPendingCards slot={generatorSlot} />
      </div>;
  }
  if (!quizList.length && !showEmptyDescription) return null;
  if (!quizList.length) {
    const shouldHideEmptyDescription = emptyDescriptionTransitionState === 'hidden';
    return <motion.div data-ai-builder-empty-description aria-hidden={shouldHideEmptyDescription} className="mb-md flex w-full flex-row items-center gap-md overflow-hidden" initial={false} animate={shouldHideEmptyDescription ? 'hidden' : 'visible'} variants={EMPTY_DESCRIPTION_VARIANTS}>
        <div className="h-[1px] grow bg-opacity-black-3" />
        <span className="text-body-md-semibold text-assistive">
          {t('build.quizList.emptyDescription')}
        </span>
        <div className="h-[1px] grow bg-opacity-black-3" />
      </motion.div>;
  }
  return <QuizListSortableContainer items={quizList.map(quiz => quiz.id)} onDragEnd={handleDragEnd} data-sentry-element="QuizListSortableContainer" data-sentry-component="QuizList" data-sentry-source-file="QuizList.tsx">
      {quizList.map((quiz, index) => {
      // EditableQuizCardWithProvider is memoized to prevent unnecessary re-renders
      // when editing individual cards. Only the edited card will re-render, not the entire list.
      // Uncomment Profiler below to verify performance optimization.
      return <Fragment key={quiz.id}>
            <EditableQuizCardWithProvider id={quiz.id} index={index} control={control} />
            {/* 자기 카드가 running 슬롯이 아니면 null 이라 목록 전체에 붙여도 원자 구독 하나뿐이다. */}
            <SimilarQuestionPendingCard quizId={quiz.id} />
          </Fragment>;
    })}
      {/* 생성기 결과는 언제나 기존 문제 뒤에 붙으므로 대기·실패 카드도 목록 끝에 둔다. */}
      {generatorSlot && <AiGenerateDockPendingCards slot={generatorSlot} />}
    </QuizListSortableContainer>;
};
const EMPTY_DESCRIPTION_VARIANTS: Variants = {
  hidden: {
    height: 0,
    marginBottom: 0,
    opacity: 0,
    y: -6,
    transition: {
      duration: 0.2,
      ease: 'easeInOut'
    },
    transitionEnd: {
      visibility: 'hidden'
    }
  },
  visible: {
    height: 'auto',
    marginBottom: 16,
    opacity: 1,
    visibility: 'visible',
    y: 0,
    transition: {
      duration: 0.22,
      ease: 'easeOut'
    }
  }
};